// apps/web/src/pages/dashboard/ReceiptsPage.tsx

import React, { useState, useRef } from 'react';
import { trpc } from '@/trpc/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, FileText, Download, Receipt, Printer, X, Phone, User, Calendar, Hash, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

import { toPng } from 'html-to-image';

const ReceiptContent = React.forwardRef<HTMLDivElement, { slip: any; isPreview?: boolean }>(({ slip, isPreview }, ref) => {
    if (!slip) return null;
    const formatCurrency = (amount: number) => `MWK ${Number(amount).toLocaleString()}`;

    return (
        <div
            ref={ref}
            className={cn(
                "pt-12 p-4 md:pt-16 md:px-10 pb-10 space-y-6 md:space-y-8 bg-white text-black",
                isPreview ? "w-full" : "min-w-[800px]"
            )}
        >
            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <h1 className="text-2xl md:text-4xl font-black tracking-tighter text-[#1e40af]">K&C <span className="text-black">LOGISTICS</span></h1>
                    <p className="text-xs md:text-sm text-gray-500 font-medium">Blantyre, Malawi</p>
                    <p className="text-xs md:text-sm text-gray-500 font-medium">+265 994 912 406</p>
                </div>
                <div className="text-right">
                    <h2 className="text-lg md:text-2xl font-bold uppercase tracking-widest text-gray-400">Cash Sale</h2>
                    <p className="text-sm md:text-xl font-mono font-bold mt-1">#{slip.slipNumber}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 pt-6 md:pt-8 border-t border-black/10">
                <div className="space-y-4">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400">Customer Details</h3>
                    <div className="space-y-1">
                        <p className="font-bold text-lg">{slip.customerName}</p>
                        <p className="text-gray-600 flex items-center gap-2 text-sm"><Phone className="w-3 h-3" /> {slip.customerPhone || 'N/A'}</p>
                    </div>
                </div>
                <div className="space-y-4 text-left md:text-right">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400">Transaction Info</h3>
                    <div className="space-y-1">
                        <p className="text-gray-600 flex items-center md:justify-end gap-2 text-xs md:text-sm"><Calendar className="w-4 h-4" /> {format(new Date(slip.createdAt), 'EEEE, MMMM dd, yyyy')}</p>
                        <p className="text-gray-600 flex items-center md:justify-end gap-2 text-xs md:text-sm"><Hash className="w-4 h-4" /> Ref: {slip.paymentRef?.slice(0, 12)}...</p>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="pt-6 md:pt-8 overflow-x-auto">
                <table className="w-full text-left">
                    <thead>
                        <tr className="border-b-2 border-black">
                            <th className="py-2 font-bold uppercase text-xs">Description</th>
                            <th className="py-2 font-bold uppercase text-xs text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="border-b border-black/5">
                            <td className="py-4 md:py-8">
                                <p className="font-bold text-base md:text-lg">{slip.description}</p>
                                {slip.booking && <p className="text-[10px] md:text-xs text-gray-500">Service for Job #{slip.booking.jobId.slice(0, 8)}</p>}
                            </td>
                            <td className="py-4 md:py-8 text-right font-bold text-lg md:text-xl whitespace-nowrap">
                                {formatCurrency(slip.amount)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Total Section */}
            <div className="flex justify-end pt-6">
                <div className="w-full md:w-64 space-y-2">
                    <div className="flex justify-between border-t-2 border-black pt-4">
                        <span className="font-black text-xl md:text-2xl uppercase italic">Total Paid</span>
                        <span className="font-black text-xl md:text-2xl whitespace-nowrap">{formatCurrency(slip.amount)}</span>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="pt-8 md:pt-12 text-center text-gray-400 text-[10px] space-y-1 uppercase tracking-widest">
                <p>Thank you for choosing K&C Logistics</p>
                <p>This is a computer-generated cash sale slip and does not require a signature.</p>
            </div>
        </div>
    );
});

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

const ReceiptView: React.FC<{ slip: any; isOpen: boolean; onClose: () => void }> = ({ slip, isOpen, onClose }) => {
    const receiptRef = useRef<HTMLDivElement>(null);

    const handleDownload = async () => {
        if (!receiptRef.current) return;

        try {
            toast.promise(
                toPng(receiptRef.current, { cacheBust: true, backgroundColor: '#ffffff', pixelRatio: 3 }),
                {
                    loading: 'Generating high-res image...',
                    success: (dataUrl) => {
                        const link = document.createElement('a');
                        link.download = `Receipt-${slip?.slipNumber || 'receipt'}.png`;
                        link.href = dataUrl;
                        link.click();
                        return 'Receipt downloaded!';
                    },
                    error: 'Failed to generate image',
                }
            );
        } catch (err) {
            console.error(err);
            toast.error("Failed to download receipt");
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl p-0 overflow-hidden bg-white border-0 shadow-2xl">
                <DialogHeader className="sr-only">
                    <DialogTitle>Cash Sale Receipt</DialogTitle>
                    <DialogDescription>
                        Visual preview of the transaction receipt.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col max-h-[90vh]">
                    <div className="p-4 border-b flex justify-between items-center bg-gray-50/80 sticky top-0 z-10 no-print">
                        <div className="flex items-center gap-2 text-[#1e40af] font-black italic">
                            <Receipt className="w-5 h-5" />
                            <span>K&C LOGISTICS</span>
                        </div>
                        <div className="flex gap-2 mr-6">
                            <Button variant="default" size="sm" onClick={handleDownload} className="gap-2 bg-[#1e40af] hover:bg-[#1e3a8a] rounded-full px-4 text-xs">
                                <Download className="w-4 h-4" />
                                <span className="hidden sm:inline">Download Image</span>
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => window.print()} className="gap-2 rounded-full px-4 text-xs">
                                <Printer className="w-4 h-4" />
                                <span className="hidden sm:inline">Print / PDF</span>
                            </Button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto bg-gray-100/30 p-4 thin-scrollbar">
                        <div className="bg-white shadow-sm border border-black/5 mx-auto max-w-[800px] mt-8 mb-8">
                            <ReceiptContent ref={receiptRef} slip={slip} isPreview={true} />
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const ReceiptsPage: React.FC = () => {
    const [selectedSlip, setSelectedSlip] = useState<any>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const directDownloadRef = useRef<HTMLDivElement>(null);
    const { data: slips, isLoading, error } = trpc.cashSaleSlip.getMySlips.useQuery();

    const handleView = (slip: any) => {
        setSelectedSlip(slip);
    };

    const handleDirectDownload = async (slip: any) => {
        if (isDownloading) return;

        setIsDownloading(true);
        // We temporarily set this as the selected slip to render it in the hidden container
        setSelectedSlip(slip);

        // Wait for render
        setTimeout(async () => {
            if (directDownloadRef.current) {
                try {
                    await toast.promise(
                        toPng(directDownloadRef.current, { cacheBust: true, backgroundColor: '#ffffff', pixelRatio: 3 }),
                        {
                            loading: `Preparing ${slip.slipNumber}...`,
                            success: (dataUrl) => {
                                const link = document.createElement('a');
                                link.download = `Receipt-${slip.slipNumber}.png`;
                                link.href = dataUrl;
                                link.click();
                                return 'Downloaded successfully!';
                            },
                            error: 'Download failed',
                        }
                    );
                } catch (err) {
                    console.error(err);
                }
            }
            setIsDownloading(false);
        }, 100);
    };

    const formatCurrency = (amount: number) => `MWK ${Number(amount).toLocaleString()}`;

    return (
        <div className="container mx-auto py-8 px-4 max-w-6xl animate-fade-in">
            {/* Hidden container for direct PNG generation */}
            <div className="fixed -left-[2000px] top-0 pointer-events-none" aria-hidden="true">
                {selectedSlip && <ReceiptContent ref={directDownloadRef} slip={selectedSlip} />}
            </div>

            <div className="flex items-center justify-between mb-8 no-print">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <Receipt className="w-8 h-8 text-primary" />
                        Cash Sale Slips
                    </h1>
                    <p className="text-muted-foreground mt-1">View and download your transaction receipts.</p>
                </div>
            </div>

            <Card className="glass overflow-hidden no-print">
                <CardHeader className="border-b border-white/10 bg-white/5">
                    <CardTitle>My Receipts</CardTitle>
                    <CardDescription>A history of all your successful payments and bookings.</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Slip Number</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead>Amount</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="h-48 text-center">
                                        <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
                                        <p className="text-sm text-muted-foreground mt-2">Loading receipts...</p>
                                    </TableCell>
                                </TableRow>
                            ) : error ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="h-48 text-center text-destructive">
                                        Failed to load receipts. Please try again later.
                                    </TableCell>
                                </TableRow>
                            ) : slips && slips.length > 0 ? (
                                slips.map((slip: any) => (
                                    <TableRow key={slip.id} className="group hover:bg-white/5 transition-colors">
                                        <TableCell className="font-mono text-sm">{slip.slipNumber}</TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span className="font-medium">{slip.description}</span>
                                                {slip.booking && (
                                                    <span className="text-[10px] text-muted-foreground">
                                                        Job: {slip.booking.job?.title || 'Unknown'}
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-bold text-green-500">
                                            {formatCurrency(slip.amount)}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground text-sm">
                                            {format(new Date(slip.createdAt), 'MMM dd, yyyy')}
                                        </TableCell>
                                        <TableCell className="text-right flex items-center justify-end gap-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="gap-2"
                                                onClick={() => handleView(slip)}
                                            >
                                                <Eye className="w-4 h-4" />
                                                View
                                            </Button>
                                            <Button
                                                variant="default"
                                                size="sm"
                                                disabled={isDownloading}
                                                className="gap-2 bg-[#1e40af] hover:bg-[#1e3a8a]"
                                                onClick={() => handleDirectDownload(slip)}
                                            >
                                                {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                                Download
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} className="h-48 text-center text-muted-foreground">
                                        <div className="flex flex-col items-center justify-center gap-2">
                                            <FileText className="w-12 h-12 opacity-20" />
                                            <p>No receipts found yet.</p>
                                            <p className="text-xs">Receipts appear here after successful payments.</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <ReceiptView
                slip={selectedSlip}
                isOpen={!!selectedSlip}
                onClose={() => setSelectedSlip(null)}
            />

            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; }
                }
            `}</style>
        </div>
    );
};

export default ReceiptsPage;
